import type {
  PostCompanyReviewExperiment2Success,
  PostCompanyReviewSuccess,
} from '@seek/ca-graphql-schema/types';
import { useAppContext, useConfig } from '@seek/libs-shared';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { LaunchDarklyFeatureKeys } from '../../shared/constants/launchDarklyFeatureFlags';
import { useFeatureFlags } from '../../shared/providers/featureFlagProvider';
import { useService } from '../../shared/providers/serviceProvider';
import type {
  ConfirmedRoles,
  RoleTitle,
} from '../../shared/services/roleService/roleService';
import {
  trackWriteAReviewSubmitted,
  type ReviewFormContext,
} from '../../shared/tracking/writeAReview/trackingEvents';
import { useOverridableFeatureFlag } from '../../shared/utils/useOverridableFeatureFlag';
import { type ReviewPayload } from '../type';
import { mapActionOrigin } from '../utils/mapActionOrigin';
import { mapReview, mapReviewExperimentTwo } from './mapper/reviewMapper';
import { WriteAReviewPage } from './WriteAReviewPage';

export const WriteAReviewContainer = () => {
  const urlSearchParams = new URL(window.location.href).searchParams;
  const companyNameQueryParam = urlSearchParams.get('cn') || undefined;
  const tracking = urlSearchParams.get('tracking') || undefined;

  const { userId } = useAppContext();

  const [roleTitleSessionId, setRoleTitleSessionId] = useState<string>(uuid());
  // Used for location search and session id for tracking
  const [sessionId] = useState<string>(uuid());
  const [experimentTwoReviewId, setExperimentTwoReviewId] =
    useState<string>('');
  // Use ReviewFormContext as state is because we may need to move the form to modal or drawer based form, having them integrated into the AppContext may cause redo in the future
  const reviewFormActionOrigin = mapActionOrigin(tracking);

  const [reviewFormContext, setReviewFormContext] = useState<ReviewFormContext>(
    {
      draftReviewId: sessionId,
      reviewId: '',
      userId,
      ...(reviewFormActionOrigin ? { reviewFormActionOrigin } : {}),
    },
  );

  const config = useConfig();

  const {
    roleService,
    companyProfileService,
    locationService,
    companyReviewService,
  } = useService();

  const getRoleTitles = async (query: string): Promise<RoleTitle[]> => {
    // If text is cleared, generate a new session id
    let currentSessionId = roleTitleSessionId;
    if (query === '') {
      currentSessionId = uuid();
      setRoleTitleSessionId(currentSessionId);
    }

    return await roleService.getRoleTitles(query, currentSessionId);
  };

  const searchCompanyProfiles = async (query: string, limit?: number) => {
    return await companyProfileService.getCompanyProfilesByName({
      name: query,
      limit,
      zone: config.zone,
    });
  };

  const searchLocations = async (query: string) => {
    return await locationService.getLocations(query, sessionId);
  };

  const getConfirmedRoles = async () => {
    return await roleService.getConfirmedRoles();
  };

  const updateRole = async (role: ConfirmedRoles) => {
    return await roleService.updateRole(role);
  };

  const { cookieFeatureFlags } = useFeatureFlags();

  const {
    isExperimentGroup,
    variantWithPrefix,
    isLoading: isFeatureFlagLoading,
  } = useOverridableFeatureFlag(
    LaunchDarklyFeatureKeys.COMPANY_REVIEW_FORM_EXPERIMENT_3,
    '4.0.1',
    cookieFeatureFlags.EXPERIMENT_TWO_REVIEW_FLOW,
  );

  useEffect(() => {
    if (variantWithPrefix && !isFeatureFlagLoading) {
      setReviewFormContext((reviewFormContext) => ({
        ...reviewFormContext,
        variant: variantWithPrefix, // this is the ONLY place variant is set, and it only changes from useOverridableFeatureFlag responses
      }));
    }
  }, [variantWithPrefix, isFeatureFlagLoading]);

  const handleSubmitReview = async (review: ReviewPayload) => {
    const mappedReview = mapReview(config.language, review);

    const response = await companyReviewService.submitReview(mappedReview);

    if ((response as PostCompanyReviewSuccess).reviewId) {
      const reviewId = (response as PostCompanyReviewSuccess).reviewId;
      // this one only updates reviewId, as variant is referenced from the existing state
      setReviewFormContext((prevContext) => ({
        ...prevContext,
        reviewId,
      }));

      if (variantWithPrefix) {
        trackWriteAReviewSubmitted({
          config,
          context: {
            ...reviewFormContext,
            reviewId,
          },
          variant: variantWithPrefix,
        });
      }
      return true;
    }

    return false;
  };

  const handleSubmitReviewExperimentTwo = async (review: ReviewPayload) => {
    const mappedReview = mapReviewExperimentTwo(review);

    const response =
      await companyReviewService.submitReviewExperimentTwo(mappedReview);

    if ((response as PostCompanyReviewExperiment2Success).reviewId) {
      setExperimentTwoReviewId(
        (response as PostCompanyReviewExperiment2Success).reviewId,
      );
      return true;
    }

    return false;
  };

  return (
    <WriteAReviewPage
      getRoleTitles={getRoleTitles}
      searchCompanyProfiles={searchCompanyProfiles}
      searchLocations={searchLocations}
      companyNameQueryParam={companyNameQueryParam}
      reviewFormContext={reviewFormContext}
      getConfirmedRoles={getConfirmedRoles}
      updateRole={updateRole}
      submitReview={handleSubmitReview}
      submitReviewExperimentTwo={handleSubmitReviewExperimentTwo}
      experimentTwoReviewId={experimentTwoReviewId}
      isExperimentTwo={isExperimentGroup}
      isFeatureFlagLoading={isFeatureFlagLoading}
    />
  );
};
